import type { UpdateUserProfilePayload } from "@/api/user-data.api";
import { normalizeGender } from "@/features/profile-completion/profile-completion";
import { COUNTRIES, COUNTRY_MAP_BY_ISO } from "@/lib/countries";
import { getRequiredLanguageFields } from "@/lib/language-profile";
import type { AuthUser, UserRole } from "@/types/auth.types";

/** Everything the settings sheets can edit. Each sheet edits a slice, the save sends the whole payload. */
export type ProfileForm = {
  name: string;
  email: string;
  username: string;
  country: string;
  gender: string;
  phoneNumber: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  spokenLanguages: string[];
  contributionLanguages: string[];
  moderationLanguages: string[];
  preferredLocale: "fr" | "en";
};

export const emptyForm = (user: AuthUser | null | undefined): ProfileForm => ({
  name: user?.name || "",
  email: user?.email || "",
  username: user?.username || user?.id || "",
  country: countryOptionName(user?.country),
  // Legacy spellings ("Homme", "F") preselect the matching option.
  gender: normalizeGender(user?.gender) ?? user?.gender ?? "",
  phoneNumber: user?.phoneNumber || "",
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
  spokenLanguages: user?.spokenLanguages ?? [],
  contributionLanguages: user?.contributionLanguages ?? [],
  moderationLanguages: user?.moderationLanguages ?? [],
  preferredLocale: user?.preferredLocale ?? "fr",
});

const optional = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const nullable = (value: string): string | null => optional(value) ?? null;

/** Same payload the page has always sent to PUT /users/:username. */
export const buildProfilePayload = (form: ProfileForm): UpdateUserProfilePayload => {
  const payload: UpdateUserProfilePayload = {
    username: form.username.trim(),
    phoneNumber: nullable(form.phoneNumber),
    spokenLanguages: form.spokenLanguages,
    contributionLanguages: form.contributionLanguages,
    moderationLanguages: form.moderationLanguages,
    preferredLocale: form.preferredLocale,
  };

  const name = optional(form.name);
  const country = optional(form.country);
  const gender = optional(form.gender);

  if (name !== undefined) payload.name = name;
  payload.email = nullable(form.email);
  if (country !== undefined) payload.country = country;
  if (gender !== undefined) payload.gender = gender;

  const currentPassword = optional(form.currentPassword);
  const newPassword = optional(form.newPassword);
  if (currentPassword && newPassword) {
    payload.currentPassword = currentPassword;
    payload.newPassword = newPassword;
  }

  return payload;
};

export type FormProblem =
  | "usernameEmpty"
  | "languagesRequired"
  | "passwordRequired"
  | "passwordMismatch"
  | "newPasswordEmpty";

/**
 * The checks run before a save, in the order the page always ran them.
 * `requirePassword` is set by the password sheet, where an empty form means nothing to save.
 */
export const validateProfileForm = (
  form: ProfileForm,
  role: UserRole | undefined,
  { requirePassword = false }: { requirePassword?: boolean } = {},
): FormProblem | null => {
  if (!form.username.trim()) return "usernameEmpty";
  if (getRequiredLanguageFields(role ?? "user").some((field) => form[field].length === 0)) return "languagesRequired";
  if (requirePassword && !form.newPassword.trim()) return "newPasswordEmpty";
  if (form.newPassword || form.confirmPassword) {
    if (!form.currentPassword) return "passwordRequired";
    if (form.newPassword !== form.confirmPassword) return "passwordMismatch";
  }
  return null;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const readMessage = (value: unknown): string | undefined => {
  if (typeof value === "string" && value.trim()) return value;
  if (Array.isArray(value)) {
    const messages = value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    return messages.length > 0 ? messages.join(", ") : undefined;
  }
  return undefined;
};

export const extractErrorMessage = (error: unknown): string | undefined => {
  if (isRecord(error)) {
    const response = error.response;
    if (isRecord(response) && isRecord(response.data)) {
      const message = readMessage(response.data.message) ?? readMessage(response.data.error);
      if (message) return message;
    }
    const direct = readMessage(error.message);
    if (direct) return direct;
  }
  return error instanceof Error ? error.message : undefined;
};

/** One or two letters for the avatar: initials of the name, else of the username. */
export const initialsOf = (user: Pick<AuthUser, "name" | "username"> | null | undefined): string => {
  const source = (user?.name || user?.username || "").trim();
  if (!source) return "?";
  const words = source.split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? words[0][0] + words[words.length - 1][0] : words[0].slice(0, 2);
  return letters.toUpperCase();
};

/** Roles worth a chip: a plain contributor needs no label on their own page. */
export const visibleRole = (role: UserRole | undefined): Exclude<UserRole, "user"> | null =>
  role && role !== "user" ? role : null;

/**
 * The country as the picker (and sign-up) store it: the English name of the
 * list. Older accounts hold a two-letter code ("BJ"): it maps to its name so
 * the picker shows it selected. Anything unknown is kept as typed.
 */
export function countryOptionName(value: string | undefined): string {
  const raw = (value ?? "").trim();
  if (!raw) return "";
  if (/^[A-Za-z]{2}$/.test(raw)) return COUNTRY_MAP_BY_ISO[raw.toUpperCase()]?.name ?? raw;
  return COUNTRIES.find((country) => country.name.toLowerCase() === raw.toLowerCase())?.name ?? raw;
}

/**
 * A stored country shown in the reader's language: "BJ" or "Benin" read
 * "Bénin" in French. Unknown values are shown as they are.
 */
export function countryName(value: string | undefined, lang: string): string | undefined {
  if (!value) return value;
  const raw = value.trim();
  const iso = /^[A-Za-z]{2}$/.test(raw)
    ? raw.toUpperCase()
    : COUNTRIES.find((country) => country.name.toLowerCase() === raw.toLowerCase())?.iso;
  if (!iso) return value;
  try {
    return new Intl.DisplayNames([lang === "en" ? "en" : "fr"], { type: "region" }).of(iso) ?? value;
  } catch {
    return value;
  }
}
